// PDF do relatório para o contador (E8): resumo do mês (fretes com as
// retenções, despesas por categoria, remuneração e agenciamentos) e as listas
// de cada um. As fontes padrão do PDF não têm o sinal de menos tipográfico:
// vai o hífen.
import { REGIMES } from './contador.js';
import { cpf as formatarCpf, data as formatarData, documento as formatarDocumento, numeroBR, placa as formatarPlaca } from './formato.js';
import { rodape } from './demonstrativo.js';
import { periodoDoRelatorio } from './pdfcaminhoes.js';
import { nomeDaPessoa } from './pdfganhos.js';

const valor = (v) => numeroBR(v, { casas: 2 });
const reais = (v) => (v < 0 ? `-R$ ${valor(-v)}` : `R$ ${valor(v)}`);
const dia = (iso) => (iso ? formatarData(iso).slice(0, 5) : '');
const FORMAS = { dinheiro: 'dinheiro', pix: 'Pix', cartao: 'cartão', boleto: 'boleto', tag: 'tag', transferencia: 'transf.', faturado: 'faturado', outro: 'outro' };

export function nomeDoArquivoDoContador({ de, ate, regime }) {
  const mes = (d) => d.slice(0, 7);
  return `contador-${mes(de) === mes(ate) ? mes(de) : `${mes(de)}-a-${mes(ate)}`}-${regime}.pdf`;
}

export async function contadorEmPdf({ relatorio: r, empresa, emitidoEm = new Date() }) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margem = 18;
  const direita = 210 - margem;
  const fim = 280;
  let y = 24;
  const caixa = r.regime === 'caixa';
  const fuso = empresa.fuso_horario || 'America/Sao_Paulo';
  const emitido = `${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(emitidoEm)}`
    + ` às ${new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(emitidoEm)}`;
  const doc = empresa.documento ? ` · ${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)}` : '';
  const limpar = (t) => String(t ?? '').replace(/−/g, '-');
  const texto = (t, x, opcoes = {}) => pdf.text(limpar(t), x, y, opcoes);
  const cabe = (altura) => {
    if (y + altura > fim) {
      pdf.addPage();
      y = 20;
      return false;
    }
    return true;
  };
  const pequeno = (t) => {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(90);
    const linhas = pdf.splitTextToSize(limpar(t), direita - margem);
    cabe(linhas.length * 3.6 + 1);
    pdf.text(linhas, margem, y);
    pdf.setTextColor(0);
    y += linhas.length * 3.6 + 1.4;
  };
  const titulo = (t) => {
    cabe(16);
    y += 2;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    texto(t, margem);
    y += 5.5;
  };
  const linhaValor = (rotulo, v, { negrito = false } = {}) => {
    cabe(6);
    pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
    pdf.setFontSize(9.5);
    texto(rotulo, margem + 2);
    texto(reais(v), direita, { align: 'right' });
    y += 5.2;
  };
  // Tabela com cabeçalho repetido a cada página. colunas: [titulo, largura, 'e'|'d']
  const tabela = (colunas, linhas, total = null) => {
    const xs = [];
    let x = margem;
    for (const [, largura] of colunas) {
      xs.push(x);
      x += largura;
    }
    const cabecalho = () => {
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(7.5);
      pdf.setTextColor(90);
      colunas.forEach(([t, largura, lado], i) => texto(t, lado === 'd' ? xs[i] + largura : xs[i], { align: lado === 'd' ? 'right' : 'left' }));
      pdf.setTextColor(0);
      y += 1.4;
      pdf.setLineWidth(0.2);
      pdf.line(margem, y, direita, y);
      y += 3.8;
    };
    const linha = (celulas, negrito = false) => {
      if (!cabe(5)) cabecalho();
      pdf.setFont('helvetica', negrito ? 'bold' : 'normal');
      pdf.setFontSize(7.5);
      celulas.forEach((c, i) => {
        const [, largura, lado] = colunas[i];
        const t = pdf.splitTextToSize(limpar(c), largura - 1.5)[0] ?? '';
        texto(t, lado === 'd' ? xs[i] + largura : xs[i], { align: lado === 'd' ? 'right' : 'left' });
      });
      y += 4.1;
    };
    cabe(12);
    cabecalho();
    for (const l of linhas) linha(l);
    if (total) {
      pdf.line(margem, y - 3, direita, y - 3);
      linha(total, true);
    }
    y += 2;
  };

  // Cabeçalho
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  texto('RELATÓRIO PARA O CONTADOR', margem);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  texto(`${empresa.nome}${doc}${empresa.rntrc ? ` · RNTRC ${empresa.rntrc}` : ''}`, margem);
  y += 5;
  texto(`Período: ${periodoDoRelatorio(r)} (${formatarData(r.de)} a ${formatarData(r.ate)}) · ${caixa ? 'regime de caixa' : 'regime de competência'}`, margem);
  y += 5;
  texto(`Emitido em ${emitido}.`, margem);
  y += 5.5;
  pequeno(`${REGIMES[r.regime]}. ${caixa
    ? 'Fretes: os quitados no período (as retenções entram aqui) e, à parte, cada adiantamento e saldo recebido. Despesas: à vista pela data; a prazo, cada parcela pela data do pagamento.'
    : 'Fretes: as cargas carregadas no período. Despesas: pela data; a compra a prazo entra inteira na data da compra.'}`
    + ' Valores em reais.');
  pdf.setLineWidth(0.3);
  pdf.line(margem, y, direita, y);
  y += 5;

  // Resumo
  const t = r.totaisCargas;
  titulo('Resumo');
  linhaValor(`Fretes ${caixa ? 'quitados' : 'carregados'} (${t.quantidade === 1 ? '1 carga' : `${t.quantidade} cargas`}): valor bruto`, t.bruto);
  if (t.pedagio) linhaValor('Pedágio embutido no frete', t.pedagio);
  if (t.estadia) linhaValor('Estadias', t.estadia);
  linhaValor('INSS retido', t.inss);
  linhaValor('SEST/SENAT retido', t.sest);
  linhaValor('IRRF retido', t.irrf);
  if (t.outros) linhaValor('Outros descontos do contratante', t.outros);
  linhaValor('Líquido dos fretes (com as estadias)', t.liquido, { negrito: true });
  if (caixa) linhaValor('Recebido no período (adiantamentos e saldos)', r.totalRecebido, { negrito: true });
  linhaValor('Despesas', r.totalDespesas, { negrito: true });
  linhaValor(caixa ? 'Pago aos colaboradores pela remuneração' : 'Remuneração dos colaboradores (acertos e folha)', r.totalRemuneracao, { negrito: true });
  if (r.agenciamentos.length) linhaValor('Agenciamentos de transportadoras', r.totalAgenciamentos, { negrito: true });

  // Fretes
  titulo(caixa ? 'Fretes quitados no período' : 'Fretes carregados no período');
  if (r.cargas.length === 0) pequeno('Nenhum.');
  else {
    tabela(
      [[caixa ? 'Quitado' : 'Data', 13, 'e'], ['Contratante', 40, 'e'], ['CT-e', 14, 'e'], ['Frete', 20, 'd'], ['Pedágio', 15, 'd'],
        ['INSS', 15, 'd'], ['SEST', 14, 'd'], ['IRRF', 15, 'd'], ['Líquido', 28, 'd']],
      r.cargas.map((c) => [dia(c.data), c.cliente || '-', c.cte ?? '', valor(c.bruto), valor(c.pedagio), valor(c.inss), valor(c.sest), valor(c.irrf), valor(c.liquido)]),
      ['Total', `${t.quantidade} cargas`, '', valor(t.bruto), valor(t.pedagio), valor(t.inss), valor(t.sest), valor(t.irrf), valor(t.liquido)],
    );
    const clientes = [...new Map(r.cargas.filter((c) => c.documentoCliente).map((c) => [c.cliente, c.documentoCliente])).entries()];
    if (clientes.length) pequeno(`Contratantes: ${clientes.map(([nome, d]) => `${nome} (${formatarDocumento(d)})`).join(' · ')}.`);
  }

  if (caixa) {
    titulo('Recebimentos no período');
    if (r.recebimentos.length === 0) pequeno('Nenhum.');
    else {
      tabela(
        [['Data', 16, 'e'], ['Contratante', 60, 'e'], ['Carga de', 20, 'e'], ['O que', 40, 'e'], ['Valor', 38, 'd']],
        r.recebimentos.map((x) => [dia(x.data), x.cliente || '-', formatarData(x.frete.data_carregamento), x.tipo === 'saldo' ? 'Saldo do frete' : 'Adiantamento', valor(x.valor)]),
        ['Total', '', '', '', valor(r.totalRecebido)],
      );
    }
  }

  // Despesas
  titulo('Despesas por categoria');
  if (r.porCategoria.length === 0) pequeno('Nenhuma.');
  else {
    tabela(
      [['Categoria', 100, 'e'], ['Lançamentos', 34, 'd'], ['Total', 40, 'd']],
      r.porCategoria.map((c) => [c.categoria, String(c.quantidade), valor(c.total)]),
      ['Total', String(r.despesas.length), valor(r.totalDespesas)],
    );
  }
  if (r.despesas.length) {
    titulo('Despesas, uma a uma');
    tabela(
      [['Data', 12, 'e'], ['Categoria', 27, 'e'], ['Veículo', 17, 'e'], ['Fornecedor / descrição', 48, 'e'], ['Nota', 16, 'e'],
        ['Pagamento', 22, 'e'], ['Foto', 9, 'e'], ['Valor', 23, 'd']],
      r.despesas.map((d) => [
        dia(d.data), d.categoria, d.veiculo ? formatarPlaca(d.veiculo) : 'geral', [d.fornecedor, d.descricao].filter(Boolean).join(' · ') || '-', d.nota ?? '',
        [d.parcela ? (d.parcela === 'a prazo' ? 'a prazo' : `parc. ${d.parcela}`) : FORMAS[d.forma] ?? '', d.pagoPor === 'colaborador' ? '(motorista)' : ''].filter(Boolean).join(' '),
        d.anexos ? 'sim' : '', valor(d.valor),
      ]),
      ['Total', '', '', '', '', '', '', valor(r.totalDespesas)],
    );
    pequeno('Foto: a nota ou o cupom está no arquivo .zip das fotos do mesmo período.');
  }

  // Remuneração
  titulo(caixa ? 'Pago aos colaboradores (vales e acertos, sem o reembolso das despesas deles)' : 'Remuneração dos colaboradores (acertos, histórico e fixo pela folha)');
  if (r.remuneracao.length === 0) pequeno('Nenhuma.');
  else {
    if (caixa) {
      tabela(
        [['Colaborador', 70, 'e'], ['CPF', 26, 'e'], ['Vales e acertos', 26, 'd'], ['Reembolso', 26, 'd'], ['Remuneração', 26, 'd']],
        r.remuneracao.map((x) => [nomeDaPessoa(x.colaborador), x.colaborador.cpf ? formatarCpf(x.colaborador.cpf) : '', valor(x.pago), valor(-x.reembolso), valor(x.valor)]),
        ['Total', '', valor(r.remuneracao.reduce((s, x) => s + Math.round(x.pago * 100), 0) / 100),
          valor(-r.remuneracao.reduce((s, x) => s + Math.round(x.reembolso * 100), 0) / 100), valor(r.totalRemuneracao)],
      );
      pequeno('Reembolso: as despesas que o colaborador pagou com o dinheiro dele ou do vale (já estão nas despesas acima); o acerto devolve esse valor.');
      if (r.historico) pequeno('Parte do período veio dos programas antigos, que não registravam os pagamentos das comissões: aqui aparecem só os vales.');
    } else {
      tabela(
        [['Colaborador', 110, 'e'], ['CPF', 30, 'e'], ['Valor', 34, 'd']],
        r.remuneracao.map((x) => [nomeDaPessoa(x.colaborador), x.colaborador.cpf ? formatarCpf(x.colaborador.cpf) : '', valor(x.valor)]),
        ['Total', '', valor(r.totalRemuneracao)],
      );
    }
  }

  if (r.agenciamentos.length) {
    titulo(caixa ? 'Agenciamentos pagos a transportadoras' : 'Agenciamentos de transportadoras (cargas do período)');
    tabela(
      [['Data', 16, 'e'], ['Contratante da carga', 70, 'e'], ['Carga de', 22, 'e'], ['Situação', 30, 'e'], ['Valor', 36, 'd']],
      r.agenciamentos.map((x) => [dia(x.data), x.cliente || '-', formatarData(x.frete.data_carregamento), x.pago ? `pago ${formatarData(x.frete.agenciamento_pago_em)}` : 'a pagar', valor(x.valor)]),
      ['Total', '', '', '', valor(r.totalAgenciamentos)],
    );
  }

  rodape(pdf, `relatório para o contador de ${periodoDoRelatorio(r)}`);
  return pdf.output('blob');
}
